import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Label, type LabelColor } from 'src/components/label';
import { useLocales } from 'src/locales';
import { useSnackbar } from 'src/components/snackbar';
import { STATUS_PALETTE, type StatusToken } from 'src/theme/status-palette';
import { acknowledgeAdminNotification, useAdminNotifications } from 'src/api/admin-notifications';
import type { AdminNotificationSeverity } from 'src/types/admin-notification';

// ----------------------------------------------------------------------

// Exported so a test can pin the mapping itself, not just `data-severity` (which a wrong mapping
// would still pass, since the attribute always echoes the raw severity regardless of colour).
export const SEVERITY_TOKEN: Record<AdminNotificationSeverity, StatusToken> = {
  warning: 'warning',
  critical: 'error',
};

// NAT-2562 review — a11y: severity was colour-only (the border/background tint). This gives it a
// visible text label too, next to the title. A separate (narrower) mapping from `SEVERITY_TOKEN`
// above: `StatusToken` covers values, such as 'complete', that are not a valid `<Label>` color.
const SEVERITY_LABEL_COLOR: Record<AdminNotificationSeverity, LabelColor> = {
  warning: 'warning',
  critical: 'error',
};

const SEVERITY_LABEL_KEY: Record<AdminNotificationSeverity, string> = {
  warning: 'notification_severity_warning',
  critical: 'notification_severity_critical',
};

/**
 * NAT-2562 — problems the backend has noticed and wants an admin to see (first: SerpApi running
 * out of searches). Rendered only when there is something to show: while loading, on an empty
 * list and on a failed read it renders nothing, so the page below never moves for it.
 *
 * "Ho visto" hides a notification until the problem recurs (the backend keeps it hidden while the
 * same occurrence is still active). The line leaves only once the backend has recorded it — no
 * optimistic removal, so a failed acknowledgement never hides a problem nobody has seen.
 */
export default function AdminNotificationsBox() {
  const { t } = useLocales('operativaDashboard');
  const { enqueueSnackbar } = useSnackbar();
  const { data, mutate } = useAdminNotifications();
  const [pending, setPending] = useState<string[]>([]);

  if (!data?.length) return null;

  const acknowledge = async (id: string) => {
    setPending((ids) => [...ids, id]);
    try {
      await acknowledgeAdminNotification(id);
      // Drop the line at once, then ask again: another admin may have seen others meanwhile.
      mutate((current) => current?.filter((n) => n.id !== id), { revalidate: true });
    } catch {
      enqueueSnackbar(t('notification_seen_failed'), { variant: 'error' });
    } finally {
      setPending((ids) => ids.filter((pendingId) => pendingId !== id));
    }
  };

  return (
    <Stack component="section" aria-label={t('notifications')} spacing={1}>
      {data.map((notification) => {
        // A severity this build does not know yet reads as a warning rather than crashing the page.
        const { light, dark } = STATUS_PALETTE[SEVERITY_TOKEN[notification.severity] ?? 'warning'];
        return (
          <Box
            key={notification.id}
            data-severity={notification.severity}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1,
              borderLeft: '4px solid',
              backgroundColor: light.bg,
              borderColor: light.accent,
              color: light.color,
              '[data-color-scheme="dark"] &': {
                backgroundColor: dark.bg,
                borderColor: dark.accent,
                color: dark.color,
              },
            }}
          >
            <Box sx={{ flex: '1 1 16rem', minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Label
                  variant="soft"
                  color={SEVERITY_LABEL_COLOR[notification.severity] ?? 'warning'}
                >
                  {t(SEVERITY_LABEL_KEY[notification.severity] ?? 'notification_severity_warning')}
                </Label>
                <Typography variant="subtitle2">{notification.title}</Typography>
              </Stack>
              <Typography variant="body2">{notification.message}</Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              loading={pending.includes(notification.id)}
              aria-label={t('notification_seen_aria', { title: notification.title })}
              onClick={() => acknowledge(notification.id)}
            >
              {t('notification_seen')}
            </Button>
          </Box>
        );
      })}
    </Stack>
  );
}
